import _ from 'underscore';

import {ClientSchemaField, createClientSchemaField} from 'registry/field/FieldTable';
import * as SchemaFieldConstants from 'schemas/fields/SchemaFieldConstants';
import {CustomSchemaInterfaceSummary} from 'schemas/schema-interface/SchemaInterfaceConstants';

import {
  MOLECULE_LOT_DISPLAY_NAME,
  MOLECULE_LOT_FIELD_NAMES,
  MOLECULE_LOT_SCHEMA_INTERFACE_ID,
} from './MoleculeLotConstants';

type MoleculeSchemaSummary = NonNullable<SchemaFieldConstants.RequiredLinkTagSchema['tagSchema']>;

/**
 * The target for the molecule link field — either a specific molecule schema or
 * a molecule-type fieldset (custom schema interface).
 */
export type MoleculeLinkTarget =
  | {type: 'schema'; schema: MoleculeSchemaSummary}
  | {type: 'fieldset'; fieldset: CustomSchemaInterfaceSummary};

const MOLECULE_LOT_SYSTEM_NAMES: Set<string> = new Set(
  Object.values(MOLECULE_LOT_FIELD_NAMES).map((f) => f.systemName)
);

function createMoleculeLotField(
  displayName: string,
  systemName: string,
  fieldType: SchemaFieldConstants.FieldType,
  overrides?: Partial<ClientSchemaField>
): ClientSchemaField {
  const base = createClientSchemaField(displayName, systemName);
  return {
    ...base,
    cid: _.uniqueId(),
    fieldType,
    appliedFieldset: {
      id: MOLECULE_LOT_SCHEMA_INTERFACE_ID,
      name: MOLECULE_LOT_DISPLAY_NAME,
    },
    ...overrides,
  };
}

function getMoleculeLinkFieldOverrides(target: MoleculeLinkTarget): Partial<ClientSchemaField> {
  const requiredLink: SchemaFieldConstants.RequiredLink =
    target.type === 'schema'
      ? {tagSchema: target.schema, entitySchemaFieldPath: null, folderItemType: null, schemaInterface: null}
      : {
          tagSchema: null,
          schemaInterface: target.fieldset,
          folderItemType: null,
          entitySchemaFieldPath: null,
        };

  return {
    isRequired: true,
    isParentLink: true,
    appliedFieldset: null,
    requiredLink,
  };
}

function getMoleculeLotEnforcedFields(target: MoleculeLinkTarget): Array<ClientSchemaField> {
  const {MOLECULE_LINK, PURITY, CONCENTRATION_MOLAR, CONCENTRATION_DENSITY} = MOLECULE_LOT_FIELD_NAMES;
  return [
    createMoleculeLotField(
      MOLECULE_LINK.displayName,
      MOLECULE_LINK.systemName,
      SchemaFieldConstants.FieldType.BIOENTITY_LINK,
      getMoleculeLinkFieldOverrides(target)
    ),
    createMoleculeLotField(PURITY.displayName, PURITY.systemName, SchemaFieldConstants.FieldType.FLOAT, {
      appliedFieldset: null,
      simpleRules: [
        {
          validationRuleTemplateId: 'svdrltmpl_floatfieldminvalue',
          constantInputs: {float_min_value: 0},
        },
        {
          validationRuleTemplateId: 'svdrltmpl_floatfieldmaxvalue',
          constantInputs: {float_max_value: 100},
        },
      ],
    }),
    createMoleculeLotField(
      CONCENTRATION_MOLAR.displayName,
      CONCENTRATION_MOLAR.systemName,
      SchemaFieldConstants.FieldType.FLOAT,
      {appliedFieldset: null}
    ),
    createMoleculeLotField(
      CONCENTRATION_DENSITY.displayName,
      CONCENTRATION_DENSITY.systemName,
      SchemaFieldConstants.FieldType.FLOAT,
      {appliedFieldset: null}
    ),
  ];
}

export function removeMoleculeLotEnforcedFields(fields: Array<ClientSchemaField>): Array<ClientSchemaField> {
  return fields.filter(
    (field) =>
      field.appliedFieldset?.id !== MOLECULE_LOT_SCHEMA_INTERFACE_ID &&
      !MOLECULE_LOT_SYSTEM_NAMES.has(field.systemName)
  );
}

export function computeUpdatedFieldsForMoleculeLotWithTarget(
  checked: boolean,
  existingFields: Array<ClientSchemaField>,
  target?: MoleculeLinkTarget
): Array<ClientSchemaField> {
  const fieldsWithoutMoleculeLot = removeMoleculeLotEnforcedFields(existingFields);

  if (!checked) {
    return fieldsWithoutMoleculeLot;
  }

  if (!target) {
    // No target provided — return fields unchanged (caller should ensure a target is selected)
    return fieldsWithoutMoleculeLot;
  }

  return [...getMoleculeLotEnforcedFields(target), ...fieldsWithoutMoleculeLot];
}
